import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Package, ShoppingCart, Users, Settings, 
  ChevronLeft, Plus, Edit, Trash2, Eye, Search, Loader2,
  Image, Save, X, Tag, Link2, DollarSign, Check, Mail, Truck, Shield, ImagePlus, FolderOpen, BarChart3, Upload, BookOpen, UserCog, AlertTriangle
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { formatPrice, formatDate, formatDateTime, cn } from '../lib/utils';
import { toast } from 'sonner';
import { MultiImageUploader, ImageUploadZone } from '../components/admin/ImageUploader';
import ExchangeModal from '../components/admin/ExchangeModal';
import EditOrderItemsModal from '../components/admin/EditOrderItemsModal';
import OrderMetaInline from '../components/admin/OrderMetaInline';
import { buildWhatsAppUrl } from '../utils/whatsapp';
import { resolveImageUrl } from '../lib/imageUrl';

const API_URL = '';

// Admin fetch helper — sends token via Authorization header
function adminFetch(url, options = {}) {
  let token = null;
  try { token = window.localStorage.getItem('bloom_admin_token'); } catch(e) {}
  if (!token) {
    const match = document.cookie.match(/bloom_admin_token=([^;]+)/);
    token = match ? match[1] : null;
  }
  const headers = { ...(options.headers || {}) };
  if (token) headers['Authorization'] = 'Bearer ' + token;
  return fetch(url, { ...options, credentials: 'include', headers });
}

const FALLBACK_CATEGORIES = [
  { id: 'nouvel-arrivage', name: 'Nouvel Arrivage' },
  { id: 'pyjama', name: 'Pyjama' },
  { id: 'sous-vetements', name: 'Sous Vetements' },
  { id: 'ensembles', name: 'Ensembles' },
  { id: 'nuisette', name: 'Nuisette' },
  { id: 'deshabille', name: 'Deshabille' },
  { id: 'kimono', name: 'Kimono' },
  { id: 'corset-body', name: 'Corset & Body' },
  { id: 'combishort', name: 'Combishort' },
  { id: 'sport', name: 'Sport' },
  { id: 'beaute', name: 'Beaute' },
  { id: 'accessoires', name: 'Accessoires' },
  { id: 'promotion', name: 'Promotion' },
];

const SIZES = ['S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

// Admin Sidebar
const AdminSidebar = () => {
  const location = useLocation();
  
  const navItems = [
    { path: '/bloom-gestion-secure', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { path: '/bloom-gestion-secure/products', icon: Package, label: 'Produits' },
    { path: '/bloom-gestion-secure/categories', icon: Tag, label: 'Catégories' },
    { path: '/bloom-gestion-secure/orders', icon: ShoppingCart, label: 'Commandes' },
    { path: '/bloom-gestion-secure/promos', icon: Tag, label: 'Codes Promo' },
    { path: '/bloom-gestion-secure/campaigns', icon: Mail, label: 'Campagnes' },
    { path: '/bloom-gestion-secure/shipping', icon: Truck, label: 'Livraisons' },
    { path: '/bloom-gestion-secure/security', icon: Shield, label: 'Securite' },
    { path: '/bloom-gestion-secure/admins', icon: UserCog, label: 'Administrateurs' },
    { path: '/bloom-gestion-secure/affiliates', icon: Users, label: 'Affilies' },
    { path: '/bloom-gestion-secure/media', icon: FolderOpen, label: 'Médias' },
    { path: '/bloom-gestion-secure/analytics', icon: BarChart3, label: 'Analytics' },
    { path: '/bloom-gestion-secure/errors', icon: AlertTriangle, label: 'Erreurs' },
    { path: '/bloom-gestion-secure/settings', icon: Settings, label: 'Paramètres' },
    { path: '/bloom-gestion-secure/shopify-import', icon: Upload, label: 'Import Shopify' },
    { path: '/bloom-gestion-secure/docs', icon: BookOpen, label: 'Documentation' },
  ];

  return (
    <aside className="admin-sidebar flex flex-col">
      <div className="p-6">
        <Link to="/" className="font-serif text-2xl">
          Bloom<span className="text-gold">.</span>
        </Link>
        <p className="text-xs text-gray-400 mt-1">Admin Panel</p>
      </div>
      
      <nav className="flex-1 py-4">
        {navItems.map((item) => {
          const isActive = item.exact 
            ? location.pathname === item.path 
            : location.pathname.startsWith(item.path);
          
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`admin-nav-item ${isActive ? 'active bg-white/10' : ''}`}
              data-testid={`admin-nav-${item.label.toLowerCase()}`}
            >
              <item.icon size={18} />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>

      <div className="p-4">
        <Link 
          to="/"
          className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
        >
          <ChevronLeft size={16} />
          Retour au site
        </Link>
      </div>
    </aside>
  );
};

// Dashboard Overview
export const AdminDashboard = () => {
  const [stats, setStats] = useState({ products: 0, orders: 0, pendingOrders: 0 });
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, ordersRes] = await Promise.all([
          adminFetch(`${API_URL}/api/bloom-gestion-secure/products?limit=1`),
          adminFetch(`${API_URL}/api/bloom-gestion-secure/orders?limit=5`)
        ]);

        if (productsRes.ok) {
          const data = await productsRes.json();
          setStats(prev => ({ ...prev, products: data.total }));
        }

        if (ordersRes.ok) {
          const data = await ordersRes.json();
          setStats(prev => ({ 
            ...prev, 
            orders: data.total,
            pendingOrders: data.orders.filter(o => o.order_status === 'pending').length
          }));
          setRecentOrders(data.orders);
        }
      } catch (error) {
        console.error('Error fetching admin data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-serif text-3xl mb-8">Dashboard</h1>

      {/* Stats */}
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6">
          <p className="text-sm text-[#6B6A68]">Total Produits</p>
          <p className="font-serif text-3xl mt-2">{loading ? '-' : stats.products}</p>
        </div>
        <div className="bg-white p-6">
          <p className="text-sm text-[#6B6A68]">Total Commandes</p>
          <p className="font-serif text-3xl mt-2">{loading ? '-' : stats.orders}</p>
        </div>
        <div className="bg-white p-6">
          <p className="text-sm text-[#6B6A68]">Commandes en attente</p>
          <p className="font-serif text-3xl mt-2 text-[#D4AF37]">{loading ? '-' : stats.pendingOrders}</p>
        </div>
      </div>

      {/* Recent Orders */}
      <div className="bg-white p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-xl">Commandes Récentes</h2>
          <Link to="/bloom-gestion-secure/orders" className="text-sm text-[#D4AF37] hover:underline">
            Voir tout
          </Link>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="animate-spin" />
          </div>
        ) : recentOrders.length === 0 ? (
          <p className="text-[#6B6A68] text-center py-8">Aucune commande</p>
        ) : (
          <div className="divide-y divide-[#E0DFDD]">
            {recentOrders.map((order) => (
              <div key={order.order_id} className="py-4 flex items-center justify-between">
                <div>
                  <p className="font-medium">#{order.order_id}</p>
                  <p className="text-sm text-[#6B6A68]">{order.customer_name}</p>
                </div>
                <div className="text-right">
                  <p className="font-medium">{formatPrice(order.total)}</p>
                  <p className="text-sm text-[#6B6A68]">{formatDate(order.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="mt-8">
        <Link 
          to="/bloom-gestion-secure/products/new"
          className="btn-primary px-6 py-3 inline-flex items-center gap-2 text-sm font-medium"
          data-testid="add-product-btn"
        >
          <Plus size={18} />
          Ajouter un produit
        </Link>
      </div>
    </div>
  );
};

// Products List
export const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);          // initial load only
  const [tableLoading, setTableLoading] = useState(false); // during refetch (search/sort/filter/page)
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [bulkCategory, setBulkCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [sortBy, setSortBy] = useState('created_at_desc');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);

  // Debounce the search input (300ms) and reset to page 1 whenever it changes.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    fetch(`${API_URL}/api/categories`)
      .then(r => r.json())
      .then(d => setCategories(d.categories || []))
      .catch(() => {});
  }, []);

  const fetchProducts = useCallback(async () => {
    setTableLoading(true);
    setError(null);
    try {
      const url = `${API_URL}/api/bloom-gestion-secure/products`
        + `?page=${currentPage}`
        + `&limit=50`
        + `&search=${encodeURIComponent(debouncedSearch)}`
        + `&status=${statusFilter}`
        + `&sort=${sortBy}`;
      const response = await adminFetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setProducts(data.products || []);
      setTotalPages(Math.max(1, data.pages || 1));
      setTotalProducts(data.total || 0);
      // If the user is currently on a page beyond the new total (e.g. after a
      // filter narrows results), snap back to the last valid page.
      if (data.pages && currentPage > data.pages) setCurrentPage(data.pages);
    } catch (e) {
      console.error('Error fetching products:', e);
      setError('Erreur de chargement des produits. Réessayez.');
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  }, [currentPage, debouncedSearch, statusFilter, sortBy]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleDelete = async (productId) => {
    if (!window.confirm('Supprimer ce produit?')) return;

    try {
      const response = await adminFetch(`${API_URL}/api/bloom-gestion-secure/products/${productId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Produit supprimé');
        fetchProducts();
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleSelect = (productId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === products.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(products.map(p => p.product_id)));
    }
  };

  const runBulkAction = async (action) => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    const labels = { activate: 'activer', deactivate: 'desactiver', delete: 'supprimer DEFINITIVEMENT' };
    if (!window.confirm(`Confirmer : ${labels[action]} ${ids.length} produit(s) ?`)) return;
    setBulkLoading(true);
    try {
      const response = await adminFetch(`${API_URL}/api/bloom-gestion-secure/products/bulk-${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_ids: ids }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${response.status}`);
      }
      const data = await response.json();
      const count = data.updated ?? data.deleted ?? 0;
      toast.success(`${count} produit(s) ${action === 'delete' ? 'supprime(s)' : action === 'activate' ? 'active(s)' : 'desactive(s)'}`);
      setSelectedIds(new Set());
      fetchProducts();
    } catch (e) {
      console.error('Bulk action error:', e);
      toast.error(`Erreur : ${e.message}`);
    } finally {
      setBulkLoading(false);
    }
  };

  const runBulkChangeCategory = async () => {
    if (selectedIds.size === 0 || !bulkCategory) return;
    const ids = Array.from(selectedIds);
    setBulkLoading(true);
    try {
      const response = await adminFetch(`${API_URL}/api/bloom-gestion-secure/products/bulk-change-category`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_ids: ids, category: bulkCategory }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${response.status}`);
      }
      const data = await response.json();
      const catName = categories.find(c => c.id === bulkCategory)?.name || bulkCategory;
      toast.success(`${data.updated || 0} produit(s) reclasse(s) dans "${catName}"`);
      setSelectedIds(new Set());
      setShowCategoryModal(false);
      setBulkCategory('');
      fetchProducts();
    } catch (e) {
      console.error('Bulk change category error:', e);
      toast.error(`Erreur : ${e.message}`);
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-3xl">Produits</h1>
        <Link 
          to="/bloom-gestion-secure/products/new"
          className="btn-primary px-4 py-2 inline-flex items-center gap-2 text-sm"
          data-testid="new-product-btn"
        >
          <Plus size={16} />
          Nouveau
        </Link>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6A68]" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher un produit..."
          className="w-full pl-10 pr-4 py-3 border border-[#E0DFDD] focus:border-[#1A1514] outline-none"
          data-testid="search-products"
        />
      </div>

      {/* Sort + Status filter row */}
      <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="products-toolbar">
        <select
          value={sortBy}
          onChange={(e) => { setSortBy(e.target.value); setCurrentPage(1); }}
          className="border border-[#E0DFDD] py-2 px-3 text-sm focus:border-[#1A1514] outline-none bg-white"
          data-testid="sort-select"
        >
          <option value="created_at_desc">Plus récents</option>
          <option value="created_at_asc">Plus anciens</option>
          <option value="name_asc">Nom (A-Z)</option>
          <option value="name_desc">Nom (Z-A)</option>
          <option value="price_asc">Prix croissant</option>
          <option value="price_desc">Prix décroissant</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
          className="border border-[#E0DFDD] py-2 px-3 text-sm focus:border-[#1A1514] outline-none bg-white"
          data-testid="status-filter"
        >
          <option value="all">Tous les produits</option>
          <option value="active">Actifs uniquement</option>
          <option value="inactive">Inactifs uniquement</option>
        </select>
        <span className="text-xs text-[#6B6A68] ml-auto" data-testid="total-count">
          {totalProducts} produit{totalProducts !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center justify-between bg-red-50 border border-red-200 text-red-700 px-4 py-3 mb-4 text-sm" data-testid="products-error">
          <span>{error}</span>
          <button
            onClick={fetchProducts}
            className="px-3 py-1 text-xs border border-red-300 hover:bg-red-100"
            data-testid="products-error-retry"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Bulk action bar (sticky when selection > 0) */}
      {selectedIds.size > 0 && (
        <div className="sticky top-0 z-10 bg-[#1A1514] text-white px-4 py-3 mb-4 flex items-center justify-between rounded" data-testid="bulk-action-bar">
          <span className="text-sm font-medium">{selectedIds.size} produit(s) selectionne(s)</span>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => runBulkAction('activate')} disabled={bulkLoading} className="px-3 py-1.5 text-xs bg-green-600 hover:bg-green-700 disabled:opacity-40" data-testid="bulk-activate-btn">Activer</button>
            <button onClick={() => runBulkAction('deactivate')} disabled={bulkLoading} className="px-3 py-1.5 text-xs bg-yellow-600 hover:bg-yellow-700 disabled:opacity-40" data-testid="bulk-deactivate-btn">Desactiver</button>
            <button onClick={() => setShowCategoryModal(true)} disabled={bulkLoading} className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-40" data-testid="bulk-change-category-btn">Changer categorie</button>
            <button onClick={() => runBulkAction('delete')} disabled={bulkLoading} className="px-3 py-1.5 text-xs bg-red-600 hover:bg-red-700 disabled:opacity-40" data-testid="bulk-delete-btn">Supprimer</button>
            <button onClick={() => setSelectedIds(new Set())} disabled={bulkLoading} className="px-3 py-1.5 text-xs bg-white/10 hover:bg-white/20" data-testid="bulk-clear-btn">Annuler</button>
          </div>
        </div>
      )}

      {/* Bulk Change Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="bulk-category-modal">
          <div className="bg-white max-w-md w-full p-6 rounded">
            <h3 className="font-serif text-xl mb-4">Changer la catégorie</h3>
            <p className="text-sm text-[#6B6A68] mb-4">{selectedIds.size} produit(s) seront reclassés dans la catégorie sélectionnée.</p>
            <select
              value={bulkCategory}
              onChange={(e) => setBulkCategory(e.target.value)}
              className="w-full border border-[#E0DFDD] p-3 text-sm focus:outline-none focus:border-[#1A1514] mb-6"
              data-testid="bulk-category-select"
            >
              <option value="">Sélectionner une catégorie...</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setShowCategoryModal(false); setBulkCategory(''); }}
                disabled={bulkLoading}
                className="px-4 py-2 text-sm border border-[#E0DFDD] hover:bg-[#F9F8F6] disabled:opacity-40"
                data-testid="bulk-category-cancel"
              >
                Annuler
              </button>
              <button
                onClick={runBulkChangeCategory}
                disabled={bulkLoading || !bulkCategory}
                className="px-4 py-2 text-sm bg-[#1A1514] text-white hover:bg-[#2A2524] disabled:opacity-40"
                data-testid="bulk-category-confirm"
              >
                {bulkLoading ? 'En cours...' : 'Reclasser'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Products Table */}
      <div className="bg-white overflow-x-auto relative" data-testid="products-table-wrapper">
        {tableLoading && !loading && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10 pointer-events-none" data-testid="products-table-overlay">
            <Loader2 className="animate-spin" />
          </div>
        )}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="animate-spin" />
          </div>
        ) : products.length === 0 ? (
          <p className="text-[#6B6A68] text-center py-12" data-testid="products-empty">Aucun produit trouvé</p>
        ) : (
          <table className="w-full">
            <thead className="border-b border-[#E0DFDD]">
              <tr>
                <th className="p-4 w-10">
                  <input
                    type="checkbox"
                    checked={products.length > 0 && selectedIds.size === products.length}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 accent-[#1A1514] cursor-pointer"
                    data-testid="select-all-checkbox"
                  />
                </th>
                <th className="text-left p-4 font-medium text-sm">Produit</th>
                <th className="text-left p-4 font-medium text-sm">Catégorie</th>
                <th className="text-left p-4 font-medium text-sm">Prix</th>
                <th className="text-left p-4 font-medium text-sm">Statut</th>
                <th className="text-left p-4 font-medium text-sm">Inventaire</th>
                <th className="text-right p-4 font-medium text-sm">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E0DFDD]">
              {products.map((product) => (
                <tr key={product.product_id} data-testid={`product-row-${product.product_id}`} className={selectedIds.has(product.product_id) ? 'bg-[#F9F8F6]' : ''}>
                  <td className="p-4">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(product.product_id)}
                      onChange={() => toggleSelect(product.product_id)}
                      className="w-4 h-4 accent-[#1A1514] cursor-pointer"
                      data-testid={`select-${product.product_id}`}
                    />
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-16 bg-[#F5F4F2] overflow-hidden">
                        {product.images?.[0]?.url && (
                          <img 
                            src={product.images[0].url} 
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      <div>
                        <p className="font-medium">{product.name}</p>
                        <p className="text-xs text-[#6B6A68]">{product.product_id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-sm capitalize">{product.category}</td>
                  <td className="p-4">
                    {product.sale_price ? (
                      <div>
                        <span className="text-[#C87979]">{formatPrice(product.sale_price)}</span>
                        <span className="text-xs text-[#6B6A68] line-through ml-2">{formatPrice(product.price)}</span>
                      </div>
                    ) : (
                      <span>{formatPrice(product.price)}</span>
                    )}
                  </td>
                  <td className="p-4">
                    <span className={`text-xs px-2 py-1 ${product.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {product.is_active ? 'Actif' : 'Inactif'}
                    </span>
                    {product.sale_price && (
                      <span className="text-xs px-2 py-1 bg-amber-50 text-amber-700 ml-1">Soldé</span>
                    )}
                    {product.affiliate_eligible === false && (
                      <span className="text-xs px-2 py-1 bg-gray-100 text-gray-500 ml-1" data-testid={`no-affiliate-${product.product_id}`}>Non-affilié</span>
                    )}
                  </td>
                  <td className="p-4" data-testid={`inventory-${product.product_id}`}>
                    {(() => {
                      const variants = product.variants || [];
                      const totalStock = variants.reduce((sum, v) => sum + (Number(v.stock) || 0), 0);
                      const variantCount = variants.length;
                      const stockColor = totalStock === 0 ? 'text-red-600' : totalStock < 10 ? 'text-amber-600' : 'text-green-700';
                      return (
                        <div className="text-xs">
                          <span className={`font-bold ${stockColor}`}>{totalStock}</span>
                          <span className="text-[#6B6A68]"> en stock</span>
                          <div className="text-[#6B6A68]">{variantCount} variante{variantCount > 1 ? 's' : ''}</div>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-2">
                      <Link 
                        to={`/product/${product.product_id}`}
                        className="p-2 hover:bg-[#F5F4F2] rounded"
                        target="_blank"
                      >
                        <Eye size={16} />
                      </Link>
                      <Link 
                        to={`/bloom-gestion-secure/products/${product.product_id}`}
                        className="p-2 hover:bg-[#F5F4F2] rounded"
                        data-testid={`edit-${product.product_id}`}
                      >
                        <Edit size={16} />
                      </Link>
                      <button 
                        onClick={() => handleDelete(product.product_id)}
                        className="p-2 hover:bg-red-50 text-[#C87979] rounded"
                        data-testid={`delete-${product.product_id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4 px-1" data-testid="products-pagination">
          <span className="text-xs text-[#6B6A68]" data-testid="pagination-summary">
            Page {currentPage} sur {totalPages} — {totalProducts} produit{totalProducts !== 1 ? 's' : ''} au total
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage <= 1 || tableLoading}
              className="px-3 py-1.5 text-xs border border-[#E0DFDD] hover:bg-[#F9F8F6] disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="page-first"
              aria-label="Première page"
            >
              «
            </button>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage <= 1 || tableLoading}
              className="px-3 py-1.5 text-xs border border-[#E0DFDD] hover:bg-[#F9F8F6] disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="page-prev"
              aria-label="Page précédente"
            >
              ‹
            </button>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages || tableLoading}
              className="px-3 py-1.5 text-xs border border-[#E0DFDD] hover:bg-[#F9F8F6] disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="page-next"
              aria-label="Page suivante"
            >
              ›
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage >= totalPages || tableLoading}
              className="px-3 py-1.5 text-xs border border-[#E0DFDD] hover:bg-[#F9F8F6] disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="page-last"
              aria-label="Dernière page"
            >
              »
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Product Form (Create/Edit)
export const AdminProductForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = location.pathname.includes('/new');
  const productId = location.pathname.split('/').pop();

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);

  useEffect(() => {
    fetch(`${API_URL}/api/categories`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.categories?.length) setCategories(d.categories); })
      .catch(() => {});
  }, []);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    sale_price: '',
    cost_price: '',
    category: '',
    material: '',
    care_instructions: '',
    images: [{ url: '', alt: '' }],
    variants: [],
    colors: [],  // [{name, hex_code, image_url}]
    selectedSizes: [],
    is_bestseller: false,
    is_new: false,
    is_bridal: false,
    is_nouvel_arrivage: false,
    is_promotion: false,
    is_active: true,
    affiliate_eligible: true
  });

  useEffect(() => {
    if (!isNew && productId) {
      const fetchProduct = async () => {
        try {
          const response = await fetch(`${API_URL}/api/products/${productId}`);
          if (response.ok) {
            const data = await response.json();
            setFormData({
              name: data.name || '',
              description: data.description || '',
              price: data.price?.toString() || '',
              sale_price: data.sale_price?.toString() || '',
              cost_price: data.cost_price?.toString() || '',
              category: data.category || '',
              material: data.material || '',
              care_instructions: data.care_instructions || '',
              images: data.images?.length ? data.images : [{ url: '', alt: '' }],
              variants: data.variants?.length ? data.variants : [],
              colors: data.colors?.length
                ? data.colors.map(c => typeof c === 'string' ? { name: c, hex_code: '#CCCCCC', image_url: '' } : c)
                : [],
              selectedSizes: data.variants?.length ? [...new Set(data.variants.map(v => v.size))] : [],
              is_bestseller: data.is_bestseller || false,
              is_new: data.is_new || false,
              is_bridal: data.is_bridal || false,
              is_nouvel_arrivage: data.is_nouvel_arrivage || false,
              is_promotion: data.is_promotion || false,
              is_active: data.is_active !== false,
              affiliate_eligible: data.affiliate_eligible !== false
            });
          }
        } catch (error) {
          console.error('Error fetching product:', error);
        } finally {
          setLoading(false);
        }
      };
      fetchProduct();
    }
  }, [isNew, productId]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: type === 'checkbox' ? checked : value };
      // Keep is_promotion and sale_price in lockstep — one is the tag,
      // the other is the discounted price. Decoupling them confuses admins
      // and stops the promo code exclusion from resetting cleanly.
      if (name === 'sale_price') {
        updated.affiliate_eligible = !value || value === '0';
        if (value && value !== '0') {
          updated.is_promotion = true;
        }
      }
      if (name === 'is_promotion' && !checked) {
        updated.sale_price = '';
        updated.affiliate_eligible = true;
      }
      return updated;
    });
  };

  const handleImageChange = (index, field, value) => {
    const newImages = [...formData.images];
    newImages[index] = { ...newImages[index], [field]: value };
    setFormData(prev => ({ ...prev, images: newImages }));
  };

  const addImage = () => {
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, { url: '', alt: '' }]
    }));
  };

  const removeImage = (index) => {
    if (formData.images.length > 1) {
      setFormData(prev => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== index)
      }));
    }
  };

  const handleVariantChange = (index, field, value) => {
    const newVariants = [...formData.variants];
    if (field === 'stock') newVariants[index] = { ...newVariants[index], stock: parseInt(value) || 0 };
    else if (field === 'price') newVariants[index] = { ...newVariants[index], price: value === '' ? null : parseFloat(value) || 0 };
    setFormData(prev => ({ ...prev, variants: newVariants }));
  };

  // Color management
  const [newColorInput, setNewColorInput] = useState('');
  const [newColorHex, setNewColorHex] = useState('#000000');

  const addColor = () => {
    const name = newColorInput.trim();
    if (!name) return;
    if (formData.colors.find(c => c.name === name)) { toast.error('Cette couleur existe déjà'); return; }
    setFormData(prev => ({ ...prev, colors: [...prev.colors, { name, hex_code: newColorHex, image_url: '' }] }));
    setNewColorInput('');
    setNewColorHex('#000000');
  };

  const removeColor = (colorName) => {
    setFormData(prev => ({
      ...prev,
      colors: prev.colors.filter(c => c.name !== colorName),
      variants: prev.variants.filter(v => v.color !== colorName),
    }));
  };

  const updateColorField = (colorName, field, value) => {
    setFormData(prev => ({
      ...prev,
      colors: prev.colors.map(c => c.name === colorName ? { ...c, [field]: value } : c)
    }));
  };

  // Size toggle
  const toggleSize = (size) => {
    setFormData(prev => {
      const has = prev.selectedSizes.includes(size);
      const newSizes = has ? prev.selectedSizes.filter(s => s !== size) : [...prev.selectedSizes, size];
      return { ...prev, selectedSizes: newSizes };
    });
  };

  // Generate all size × color combinations
  const generateVariants = () => {
    const activeColors = formData.colors.filter(c => c.name?.trim());
    const activeSizes = formData.selectedSizes.filter(s => s);
    if (activeColors.length === 0) { toast.error('Ajoutez au moins une couleur'); return; }
    if (activeSizes.length === 0) { toast.error('Cochez au moins une taille'); return; }
    const newVariants = [];
    for (const color of activeColors) {
      for (const size of SIZES.filter(s => activeSizes.includes(s))) {
        const existing = formData.variants.find(v => v.size === size && v.color === color.name);
        newVariants.push({
          size,
          color: color.name,
          stock: existing?.stock ?? 0,
          price: existing?.price ?? null,
        });
      }
    }
    setFormData(prev => ({ ...prev, variants: newVariants }));
    toast.success(`${newVariants.length} variants générés`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Pre-validate critical fields with friendly toasts
    if (!formData.category || !categories.find(c => c.id === formData.category)) {
      toast.error('Catégorie invalide — sélectionnez une catégorie existante avant de sauvegarder.');
      return;
    }

    setSaving(true);

    const payload = {
      ...formData,
      price: parseFloat(formData.price),
      sale_price: formData.sale_price ? parseFloat(formData.sale_price) : null,
      cost_price: formData.cost_price ? parseFloat(formData.cost_price) : 0,
      images: formData.images.filter(img => img.url).map(img => ({ url: img.url, alt: img.alt || '', file_id: img.file_id || '', thumbnail_url: img.thumbnail_url || '' })),
      colors: formData.colors.filter(c => c),
      selectedSizes: undefined,
    };

    try {
      const url = isNew 
        ? `${API_URL}/api/bloom-gestion-secure/products`
        : `${API_URL}/api/bloom-gestion-secure/products/${productId}`;
      
      const response = await adminFetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(isNew ? 'Produit créé' : 'Produit mis à jour');
        navigate('/bloom-gestion-secure/products');
      } else {
        let detail = `Erreur ${response.status}`;
        try {
          const err = await response.json();
          detail = err.detail || (Array.isArray(err.detail) ? err.detail.map(e => e.msg).join(', ') : detail);
          if (typeof err.detail === 'object') detail = JSON.stringify(err.detail);
        } catch {}
        console.error('Save error:', detail);
        toast.error(detail);
      }
    } catch (error) {
      console.error('Save exception:', error);
      toast.error('Erreur réseau — vérifiez votre connexion.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-product-form">
      <div className="flex items-center gap-4 mb-8">
        <button onClick={() => navigate('/bloom-gestion-secure/products')} className="p-2 hover:bg-[#F5F4F2] rounded">
          <ChevronLeft size={20} />
        </button>
        <h1 className="font-serif text-3xl">{isNew ? 'Nouveau Produit' : 'Modifier Produit'}</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white p-6">
              <h2 className="font-medium mb-4">Informations générales</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-[#6B6A68] block mb-2">Nom du produit *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="checkout-input"
                    data-testid="input-name"
                    required
                  />
                </div>

                <div>
                  <label className="text-sm text-[#6B6A68] block mb-2">Description *</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={4}
                    className="checkout-input resize-none"
                    data-testid="input-description"
                    required
                  />
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm text-[#6B6A68] block mb-2">Prix (DA) *</label>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      min="0"
                      step="100"
                      className="checkout-input"
                      data-testid="input-price"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm text-[#6B6A68] block mb-2">Prix promo (DA)</label>
                    <input
                      type="number"
                      name="sale_price"
                      value={formData.sale_price}
                      onChange={handleInputChange}
                      min="0"
                      step="100"
                      className="checkout-input"
                      data-testid="input-sale-price"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-[#6B6A68] block mb-2">Coût d'achat (DA)</label>
                    <input
                      type="number"
                      name="cost_price"
                      value={formData.cost_price}
                      onChange={handleInputChange}
                      min="0"
                      step="100"
                      className="checkout-input"
                      data-testid="input-cost-price"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm text-[#6B6A68] block mb-2">Catégorie *</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="checkout-input bg-transparent"
                    data-testid="select-category"
                    required
                  >
                    <option value="">Sélectionner</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                    {formData.category && !categories.find(c => c.id === formData.category) && (
                      <option value={formData.category}>⚠ {formData.category} (catégorie obsolète — choisir une autre)</option>
                    )}
                  </select>
                  {formData.category && !categories.find(c => c.id === formData.category) && (
                    <p className="text-xs text-amber-600 mt-1">Cette catégorie n'existe plus. Sélectionnez-en une autre pour pouvoir sauvegarder.</p>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-[#6B6A68] block mb-2">Matière</label>
                    <input
                      type="text"
                      name="material"
                      value={formData.material}
                      onChange={handleInputChange}
                      className="checkout-input"
                      data-testid="input-material"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-[#6B6A68] block mb-2">Entretien</label>
                    <input
                      type="text"
                      name="care_instructions"
                      value={formData.care_instructions}
                      onChange={handleInputChange}
                      className="checkout-input"
                      data-testid="input-care"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Images */}
            <div className="bg-white p-6">
              <h2 className="font-medium mb-4">Images du produit</h2>
              <MultiImageUploader
                images={formData.images}
                onChange={(newImages) => setFormData(prev => ({ ...prev, images: newImages }))}
                uploadType="products"
                maxImages={10}
              />
            </div>

            {/* 1. Couleurs — with hex picker + photo assignment */}
            <div className="bg-white p-6">
              <h2 className="font-medium mb-4">Couleurs</h2>

              {/* Existing colors */}
              {formData.colors.length > 0 && (
                <div className="space-y-3 mb-4">
                  {formData.colors.map((color) => (
                    <div key={color.name} className="flex items-center gap-3 p-3 bg-[#F9F8F6] border border-[#E0DFDD]" data-testid={`color-row-${color.name}`}>
                      <div className="w-8 h-8 rounded-full border border-[#E0DFDD] flex-shrink-0" style={{ backgroundColor: color.hex_code }} />
                      <span className="text-sm font-medium flex-shrink-0 w-24">{color.name}</span>
                      <input
                        type="color"
                        value={color.hex_code}
                        onChange={(e) => updateColorField(color.name, 'hex_code', e.target.value)}
                        className="w-8 h-8 cursor-pointer border-0 p-0 flex-shrink-0"
                        title="Changer la teinte"
                        data-testid={`color-picker-${color.name}`}
                      />
                      {/* Photo selector — pick from uploaded images */}
                      {formData.images.filter(img => img.url).length > 0 ? (
                        <div className="flex items-center gap-2 flex-1 overflow-x-auto">
                          <span className="text-xs text-[#6B6A68] flex-shrink-0">Photo:</span>
                          {formData.images.filter(img => img.url).map((img, imgIdx) => (
                            <button
                              key={imgIdx}
                              type="button"
                              onClick={() => updateColorField(color.name, 'image_url', img.url)}
                              className={cn(
                                "w-10 h-12 flex-shrink-0 border-2 overflow-hidden transition-all",
                                color.image_url === img.url ? "border-[#D4AF37] ring-1 ring-[#D4AF37]" : "border-transparent opacity-60 hover:opacity-100"
                              )}
                              data-testid={`color-img-${color.name}-${imgIdx}`}
                            >
                              <img src={resolveImageUrl(img.url)} alt="" className="w-full h-full object-cover" />
                            </button>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-[#6B6A68]">Uploadez des images d'abord</span>
                      )}
                      <button type="button" onClick={() => removeColor(color.name)} className="text-[#C87979] hover:text-red-600 flex-shrink-0 ml-auto" data-testid={`remove-color-${color.name}`}>
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add new color */}
              <div className="flex gap-2 items-center">
                <input
                  type="color"
                  value={newColorHex}
                  onChange={(e) => setNewColorHex(e.target.value)}
                  className="w-10 h-10 cursor-pointer border border-[#E0DFDD] p-0.5 flex-shrink-0"
                  data-testid="new-color-hex"
                />
                <input
                  type="text"
                  value={newColorInput}
                  onChange={(e) => setNewColorInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addColor())}
                  placeholder="Nom de la couleur (ex: Rouge, Noir...)"
                  className="flex-1 border border-[#E0DFDD] px-3 py-2.5 text-sm focus:outline-none focus:border-[#1A1514]"
                  data-testid="new-color-input"
                />
                <button type="button" onClick={addColor} className="px-4 py-2.5 bg-[#1A1514] text-white text-sm font-medium flex-shrink-0" data-testid="add-color-btn">
                  Ajouter
                </button>
              </div>
            </div>

            {/* 2. Tailles — cochables, visible après au moins 1 couleur */}
            {formData.colors.length > 0 && (
              <div className="bg-white p-6">
                <h2 className="font-medium mb-4">Tailles disponibles pour ce produit</h2>
                <div className="flex flex-wrap gap-3">
                  {SIZES.map((size) => {
                    const isChecked = formData.selectedSizes.includes(size);
                    return (
                      <button
                        key={size}
                        type="button"
                        onClick={() => toggleSize(size)}
                        className={cn(
                          "w-16 h-10 text-sm font-medium border transition-colors",
                          isChecked
                            ? "bg-[#1A1514] text-white border-[#1A1514]"
                            : "bg-white text-[#6B6A68] border-[#E0DFDD] hover:border-[#1A1514]"
                        )}
                        data-testid={`toggle-size-${size}`}
                      >
                        {size}
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  onClick={generateVariants}
                  disabled={formData.selectedSizes.length === 0}
                  className="mt-4 w-full bg-[#D4AF37] text-white py-2.5 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-40"
                  data-testid="generate-variants-btn"
                >
                  Générer les variants ({formData.colors.length} couleurs × {formData.selectedSizes.length} tailles)
                </button>
              </div>
            )}

            {/* 3. Variants Table — grouped by color */}
            {formData.variants.length > 0 && (
              <div className="bg-white p-6">
                <h2 className="font-medium mb-4">Variants — Stock & Prix ({formData.variants.length})</h2>
                {(() => {
                  const colorGroups = {};
                  formData.variants.forEach((v, i) => {
                    if (!colorGroups[v.color]) colorGroups[v.color] = [];
                    colorGroups[v.color].push({ ...v, _index: i });
                  });
                  return Object.entries(colorGroups).map(([color, variants]) => (
                    <div key={color} className="mb-4 last:mb-0">
                      <div className="flex items-center gap-2 mb-2 px-2 py-1.5 bg-[#F5F4F2] border-l-4 border-[#D4AF37]">
                        <span className="text-sm font-semibold">{color}</span>
                        <span className="text-xs text-[#6B6A68]">({variants.length} tailles)</span>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-xs text-[#6B6A68]">
                              <th className="text-left p-1.5 font-medium w-20">Taille</th>
                              <th className="text-right p-1.5 font-medium w-24">Stock</th>
                              <th className="text-right p-1.5 font-medium w-28">Prix (DA)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {variants.map((variant) => (
                              <tr key={`${variant.size}-${variant.color}`} className={cn("border-t border-[#F0EFED]", variant.stock === 0 && "opacity-40")}>
                                <td className="p-1.5 font-medium">{variant.size}</td>
                                <td className="p-1.5">
                                  <input
                                    type="number"
                                    value={variant.stock}
                                    onChange={(e) => handleVariantChange(variant._index, 'stock', e.target.value)}
                                    min="0"
                                    className="w-full border border-[#E0DFDD] p-1.5 text-center text-sm focus:outline-none focus:border-[#1A1514]"
                                    data-testid={`variant-stock-${variant.size}-${variant.color}`}
                                  />
                                </td>
                                <td className="p-1.5">
                                  <input
                                    type="number"
                                    value={variant.price ?? ''}
                                    onChange={(e) => handleVariantChange(variant._index, 'price', e.target.value)}
                                    min="0"
                                    step="100"
                                    placeholder={formData.price || 'Principal'}
                                    className="w-full border border-[#E0DFDD] p-1.5 text-center text-sm focus:outline-none focus:border-[#1A1514]"
                                    data-testid={`variant-price-${variant.size}-${variant.color}`}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-white p-6">
              <h2 className="font-medium mb-4">Statut</h2>
              
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  className="w-4 h-4 accent-[#1A1514]"
                  data-testid="checkbox-active"
                />
                <span className="text-sm">Produit actif</span>
              </label>
            </div>

            <div className="bg-white p-6">
              <h2 className="font-medium mb-4">Labels</h2>
              
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_nouvel_arrivage"
                    checked={formData.is_nouvel_arrivage}
                    onChange={handleInputChange}
                    className="w-4 h-4 accent-[#C87979]"
                    data-testid="checkbox-nouvel-arrivage"
                  />
                  <span className="text-sm font-medium text-[#C87979]">Nouvel Arrivage</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_promotion"
                    checked={formData.is_promotion}
                    onChange={handleInputChange}
                    className="w-4 h-4 accent-[#C87979]"
                    data-testid="checkbox-promotion"
                  />
                  <span className="text-sm font-medium text-[#C87979]">Promotion</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_new"
                    checked={formData.is_new}
                    onChange={handleInputChange}
                    className="w-4 h-4 accent-[#1A1514]"
                    data-testid="checkbox-new"
                  />
                  <span className="text-sm">Nouveau</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_bestseller"
                    checked={formData.is_bestseller}
                    onChange={handleInputChange}
                    className="w-4 h-4 accent-[#1A1514]"
                    data-testid="checkbox-bestseller"
                  />
                  <span className="text-sm">Meilleure vente</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_bridal"
                    checked={formData.is_bridal}
                    onChange={handleInputChange}
                    className="w-4 h-4 accent-[#1A1514]"
                    data-testid="checkbox-bridal"
                  />
                  <span className="text-sm">Collection Mariée</span>
                </label>
              </div>
            </div>

            <div className="bg-white p-6">
              <h2 className="font-medium mb-4">Programme Affiliation</h2>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="affiliate_eligible"
                  checked={formData.affiliate_eligible}
                  onChange={handleInputChange}
                  className="w-4 h-4 accent-[#D4AF37] mt-0.5"
                  data-testid="checkbox-affiliate-eligible"
                />
                <div>
                  <span className="text-sm">Éligible au programme d'affiliation</span>
                  {formData.sale_price && (
                    <p className="text-xs text-[#C87979] mt-1">
                      Désactivé automatiquement (article en promotion)
                    </p>
                  )}
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Submit button — bottom of form */}
        <button
          type="submit"
          disabled={saving}
          className="btn-primary w-full py-4 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50 mt-6"
          data-testid="save-product-btn"
        >
          {saving ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <Save size={18} />
          )}
          {isNew ? 'Créer le produit' : 'Sauvegarder'}
        </button>
      </form>
    </div>
  );
};

// Orders Management
export const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);              // initial load
  const [tableLoading, setTableLoading] = useState(false);   // refetch overlay
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState('created_at_desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [exchangeOrder, setExchangeOrder] = useState(null);
  const [editItemsOrder, setEditItemsOrder] = useState(null);

  // Debounce search input (300ms) and reset to page 1 on every change.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchOrders = useCallback(async () => {
    setTableLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(currentPage),
        limit: '50',
        sort: sortBy,
      });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (filter && filter !== 'all') params.set('status', filter);
      const url = `${API_URL}/api/bloom-gestion-secure/orders?${params.toString()}`;
      const response = await adminFetch(url, { credentials: 'include' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setOrders(data.orders || []);
      setTotalPages(Math.max(1, data.pages || 1));
      setTotalOrders(data.total || 0);
      if (data.pages && currentPage > data.pages) setCurrentPage(data.pages);
    } catch (e) {
      console.error('Error fetching orders:', e);
      setError('Erreur de chargement des commandes. Réessayez.');
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  }, [currentPage, debouncedSearch, filter, sortBy]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const updateOrderStatus = async (orderId, status) => {
    try {
      const response = await adminFetch(`${API_URL}/api/bloom-gestion-secure/orders/${orderId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ order_status: status })
      });

      if (response.ok) {
        toast.success('Statut mis à jour');
        fetchOrders();
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const callWorkflow = async (orderId, action, body = {}) => {
    try {
      const response = await adminFetch(`${API_URL}/api/bloom-gestion-secure/orders/${orderId}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${response.status}`);
      }
      const data = await response.json();
      const labels = { confirm: 'confirmee', fulfill: 'expediee (etiquette creee)', cancel: 'annulee' };
      toast.success(`Commande #${orderId} ${labels[action] || 'mise a jour'}`);
      if (action === 'fulfill' && data.tracking) {
        toast.success(`Tracking: ${data.tracking}`);
      }
      if (action === 'cancel' && data.restocked?.length) {
        toast.success(`${data.restocked.length} variante(s) restockee(s)`);
      }
      fetchOrders();
    } catch (e) {
      console.error(`${action} error:`, e);
      toast.error(`Erreur : ${e.message}`);
    }
  };

  const handleConfirm = (orderId) => {
    if (!window.confirm('Confirmer la commande après avoir parlé avec la cliente ?')) return;
    callWorkflow(orderId, 'confirm');
  };

  const handleFulfill = (orderId) => {
    if (!window.confirm('Préparer & expédier ? Cela enverra la commande à World Express et créera l\'étiquette.')) return;
    callWorkflow(orderId, 'fulfill');
  };

  const handleCancel = (orderId) => {
    const reason = window.prompt('Raison de l\'annulation ? (optionnel)') ?? '';
    if (reason === null) return;
    if (!window.confirm('Annuler cette commande et restocker l\'inventaire ?')) return;
    callWorkflow(orderId, 'cancel', { reason, restock: true });
  };

  const saveOrderMeta = async (orderId, patch) => {
    try {
      const r = await adminFetch(`${API_URL}/api/bloom-gestion-secure/orders/${orderId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success('Notes enregistrées');
      setOrders((prev) => prev.map((o) => (o.order_id === orderId ? { ...o, ...patch } : o)));
    } catch (e) {
      toast.error(`Erreur : ${e.message}`);
    }
  };

  const handleMarkReturnReceived = async (orderId) => {
    const notes = window.prompt('Notes sur le retour reçu (optionnel) ?') ?? '';
    if (notes === null) return;
    try {
      const r = await adminFetch(`${API_URL}/api/bloom-gestion-secure/orders/${orderId}/mark-return-received`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const data = await r.json();
      toast.success(`Retour marque recu — ${data.restocked?.length || 0} variante(s) restockee(s)`);
      fetchOrders();
    } catch (e) {
      console.error('Mark return error:', e);
      toast.error(`Erreur : ${e.message}`);
    }
  };

  const STATUS_LABELS = {
    pending: { label: 'En attente', color: 'bg-amber-100 text-amber-800' },
    en_attente: { label: 'En attente', color: 'bg-amber-100 text-amber-800' },
    confirmed: { label: 'Confirmée', color: 'bg-blue-100 text-blue-800' },
    processing: { label: 'En préparation', color: 'bg-blue-100 text-blue-800' },
    en_preparation: { label: 'En préparation', color: 'bg-blue-100 text-blue-800' },
    fulfilled: { label: 'Expédiée', color: 'bg-purple-100 text-purple-800' },
    shipped: { label: 'Expédiée', color: 'bg-purple-100 text-purple-800' },
    delivered: { label: 'Livrée', color: 'bg-green-100 text-green-800' },
    livre: { label: 'Livrée', color: 'bg-green-100 text-green-800' },
    returned: { label: 'Retournée', color: 'bg-orange-100 text-orange-800' },
    cancelled: { label: 'Annulée', color: 'bg-red-100 text-red-800' },
    exchange_created: { label: 'Échange créé', color: 'bg-indigo-100 text-indigo-800' },
    exchange_shipped: { label: 'Échange expédié', color: 'bg-indigo-100 text-indigo-800' },
  };

  const STATUS_OPTIONS = [
    { value: 'pending', label: 'En attente' },
    { value: 'confirmed', label: 'Confirmée' },
    { value: 'fulfilled', label: 'Expédiée' },
    { value: 'delivered', label: 'Livrée' },
    { value: 'returned', label: 'Retournée' },
    { value: 'cancelled', label: 'Annulée' },
    { value: 'exchange', label: 'Échanges' },
  ];

  return (
    <div data-testid="admin-orders">
      <h1 className="font-serif text-3xl mb-8">Commandes</h1>

      {/* Search input */}
      <div className="relative mb-4">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6A68]" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher par nom, téléphone, email, n° commande, wilaya, adresse..."
          className="w-full pl-10 pr-4 py-3 border border-[#E0DFDD] focus:border-[#1A1514] outline-none"
          data-testid="search-orders"
        />
      </div>

      {/* Sort + total */}
      <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="orders-toolbar">
        <select
          value={sortBy}
          onChange={(e) => { setSortBy(e.target.value); setCurrentPage(1); }}
          className="border border-[#E0DFDD] py-2 px-3 text-sm focus:border-[#1A1514] outline-none bg-white"
          data-testid="orders-sort-select"
        >
          <option value="created_at_desc">Plus récentes</option>
          <option value="created_at_asc">Plus anciennes</option>
          <option value="total_desc">Montant décroissant</option>
          <option value="total_asc">Montant croissant</option>
        </select>
        <span className="text-xs text-[#6B6A68] ml-auto" data-testid="orders-total-count">
          {totalOrders} commande{totalOrders !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center justify-between bg-red-50 border border-red-200 text-red-700 px-4 py-3 mb-4 text-sm" data-testid="orders-error">
          <span>{error}</span>
          <button
            onClick={fetchOrders}
            className="px-3 py-1 text-xs border border-red-300 hover:bg-red-100"
            data-testid="orders-error-retry"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        <button
          onClick={() => { setFilter('all'); setCurrentPage(1); }}
          className={`px-4 py-2 text-sm whitespace-nowrap ${filter === 'all' ? 'bg-[#1A1514] text-white' : 'bg-white border border-[#E0DFDD]'}`}
        >
          Toutes
        </button>
        {STATUS_OPTIONS.map((status) => (
          <button
            key={status.value}
            onClick={() => { setFilter(status.value); setCurrentPage(1); }}
            className={`px-4 py-2 text-sm whitespace-nowrap ${filter === status.value ? 'bg-[#1A1514] text-white' : 'bg-white border border-[#E0DFDD]'}`}
          >
            {status.label}
          </button>
        ))}
      </div>

      {/* Orders List */}
      <div className="bg-white relative" data-testid="orders-list-wrapper">
        {tableLoading && !loading && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10 pointer-events-none" data-testid="orders-overlay">
            <Loader2 className="animate-spin" />
          </div>
        )}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="animate-spin" />
          </div>
        ) : orders.length === 0 ? (
          <p className="text-[#6B6A68] text-center py-12" data-testid="orders-empty">Aucune commande</p>
        ) : (
          <div className="divide-y divide-[#E0DFDD]">
            {orders.map((order) => (
              <div key={order.order_id} className="p-6" data-testid={`order-${order.order_id}`}>
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <p className="font-medium">#{order.order_id}</p>
                      <a
                        href={`/bloom-gestion-secure/orders/${order.order_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs px-2 py-0.5 border border-[#1A1514] text-[#1A1514] hover:bg-[#1A1514] hover:text-white transition-colors inline-flex items-center gap-1"
                        data-testid={`open-detail-${order.order_id}`}
                        title="Ouvrir le détail dans un nouvel onglet"
                      >
                        Ouvrir ↗
                      </a>
                    </div>
                    <p className="text-sm text-[#6B6A68]">{order.customer_name} • {order.customer_phone}</p>
                    <p className="text-sm text-[#6B6A68]">{order.wilaya} - {order.shipping_address}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-serif text-xl">{formatPrice(order.total)}</p>
                    <p className="text-xs text-[#6B6A68]" data-testid={`order-datetime-${order.order_id}`}>{formatDateTime(order.created_at)}</p>
                  </div>
                </div>

                {/* Items */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {order.items.map((item, idx) => (
                    <div key={idx} className="text-xs bg-[#F5F4F2] px-2.5 py-1.5 flex items-center gap-2 border border-[#E0DFDD]" data-testid={`order-item-${order.order_id}-${idx}`}>
                      <span className="font-medium">{item.name}</span>
                      <span className="text-[#6B6A68]">×{item.quantity}</span>
                      <span className="bg-white px-1.5 py-0.5 border border-[#E0DFDD] font-mono text-[10px] uppercase">{item.size || '—'}</span>
                      {item.color ? (
                        <span className="bg-[#1A1514] text-[#D4AF37] px-1.5 py-0.5 font-medium text-[10px] uppercase tracking-wider" data-testid={`order-item-color-${order.order_id}-${idx}`}>
                          {item.color}
                        </span>
                      ) : (
                        <span className="text-[10px] text-[#9E9C9A] italic">sans couleur</span>
                      )}
                      <span className="text-[10px] text-[#9E9C9A] font-mono">{item.product_id}</span>
                    </div>
                  ))}
                </div>

                {/* Status badge + tracking */}
                <div className="flex items-center flex-wrap gap-3 mb-3">
                  {(() => {
                    const meta = STATUS_LABELS[order.order_status] || { label: order.order_status, color: 'bg-gray-100 text-gray-700' };
                    return <span className={`text-xs px-3 py-1.5 font-medium ${meta.color}`} data-testid={`status-badge-${order.order_id}`}>{meta.label}</span>;
                  })()}
                  {order.ecotrack_tracking && (
                    <span className="text-xs text-[#6B6A68]" data-testid={`tracking-${order.order_id}`}>
                      Tracking: <span className="font-mono">{order.ecotrack_tracking}</span>
                    </span>
                  )}
                  <span className="text-xs text-[#6B6A68]">
                    {order.payment_method === 'cod' ? 'COD' : 'Stripe'}
                    {order.payment_status === 'paid' && ' (Payé)'}
                  </span>
                </div>

                {/* Inline admin meta: social media + note (editable directly on the list) */}
                <OrderMetaInline order={order} onSave={(patch) => saveOrderMeta(order.order_id, patch)} />

                {/* Workflow action buttons */}
                <div className="flex flex-wrap gap-2">
                  {(order.order_status === 'pending' || order.order_status === 'en_attente') && (
                    <button
                      onClick={() => handleConfirm(order.order_id)}
                      className="px-4 py-2 text-xs font-medium bg-blue-600 text-white hover:bg-blue-700"
                      data-testid={`confirm-btn-${order.order_id}`}
                    >
                      ✓ Confirmer (apres appel client)
                    </button>
                  )}
                  {(order.order_status === 'pending' || order.order_status === 'en_attente' || order.order_status === 'confirmed') && !order.ecotrack_tracking && (
                    <button
                      onClick={() => handleFulfill(order.order_id)}
                      className="px-4 py-2 text-xs font-medium bg-purple-600 text-white hover:bg-purple-700"
                      data-testid={`fulfill-btn-${order.order_id}`}
                    >
                      📦 Préparer & expédier (envoie à World Express)
                    </button>
                  )}
                  {(order.order_status === 'pending' || order.order_status === 'en_attente' || order.order_status === 'confirmed' || order.order_status === 'en_preparation' || order.order_status === 'processing') && order.ecotrack_tracking && (
                    <button
                      onClick={() => handleFulfill(order.order_id)}
                      className="px-4 py-2 text-xs font-medium bg-purple-600 text-white hover:bg-purple-700"
                      data-testid={`fulfill-btn-${order.order_id}`}
                    >
                      📦 Marquer expédiée (tracking déjà créé)
                    </button>
                  )}
                  {(order.order_status === 'fulfilled' || order.order_status === 'shipped') && order.ecotrack_tracking && (
                    <a
                      href={`/api/bloom-gestion-secure/shipping/${order.order_id}/label`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 text-xs font-medium border border-[#1A1514] hover:bg-[#F9F8F6]"
                      data-testid={`label-btn-${order.order_id}`}
                    >
                      📄 Etiquette
                    </a>
                  )}
                  {order.order_type === 'exchange' && !order.return_received && (
                    <button
                      onClick={() => handleMarkReturnReceived(order.order_id)}
                      className="px-4 py-2 text-xs font-medium bg-orange-600 text-white hover:bg-orange-700"
                      data-testid={`mark-return-btn-${order.order_id}`}
                    >
                      📥 Marquer retour reçu (restock)
                    </button>
                  )}
                  {order.order_type !== 'exchange' && !order.has_exchange && (
                    <button
                      onClick={() => setExchangeOrder(order)}
                      className="px-4 py-2 text-xs font-medium border border-blue-300 text-blue-600 hover:bg-blue-50"
                      data-testid={`exchange-btn-${order.order_id}`}
                    >
                      🔄 Echanger
                    </button>
                  )}
                  {!['fulfilled', 'shipped', 'delivered', 'livre', 'returned', 'cancelled'].includes(order.order_status) && (
                    <button
                      onClick={() => setEditItemsOrder(order)}
                      className="px-4 py-2 text-xs font-medium border border-amber-300 text-amber-700 hover:bg-amber-50"
                      data-testid={`edit-items-btn-${order.order_id}`}
                    >
                      ✏️ Modifier les articles
                    </button>
                  )}
                  {order.customer_phone && (
                    <a
                      href={buildWhatsAppUrl(order, 'auto')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 text-xs font-medium bg-green-600 text-white hover:bg-green-700 inline-flex items-center gap-1.5"
                      data-testid={`whatsapp-btn-${order.order_id}`}
                      title={`Envoyer un message WhatsApp à ${order.customer_name}`}
                    >
                      💬 WhatsApp
                    </a>
                  )}
                  {order.has_exchange && order.exchanged_to && (
                    <span className="px-3 py-1.5 text-xs bg-blue-50 text-blue-700">
                      ↔ Echangée → #{order.exchanged_to}
                    </span>
                  )}
                  {order.exchange_of && (
                    <span className="px-3 py-1.5 text-xs bg-blue-50 text-blue-700">
                      ↔ Echange de #{order.exchange_of}
                    </span>
                  )}
                  {order.order_status !== 'cancelled' && order.order_status !== 'delivered' && order.order_status !== 'livre' && order.order_status !== 'returned' && (
                    <button
                      onClick={() => handleCancel(order.order_id)}
                      className="px-4 py-2 text-xs font-medium border border-red-300 text-red-600 hover:bg-red-50"
                      data-testid={`cancel-btn-${order.order_id}`}
                    >
                      ✕ Annuler {order.ecotrack_tracking ? '(restock + Ecotrack)' : '(restock)'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {exchangeOrder && (
        <ExchangeModal
          order={exchangeOrder}
          onClose={() => setExchangeOrder(null)}
          onSuccess={fetchOrders}
        />
      )}
      {editItemsOrder && (
        <EditOrderItemsModal
          order={editItemsOrder}
          onClose={() => setEditItemsOrder(null)}
          onSuccess={fetchOrders}
        />
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4 px-1" data-testid="orders-pagination">
          <span className="text-xs text-[#6B6A68]" data-testid="orders-pagination-summary">
            Page {currentPage} sur {totalPages} — {totalOrders} commande{totalOrders !== 1 ? 's' : ''} au total
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage <= 1 || tableLoading}
              className="px-3 py-1.5 text-xs border border-[#E0DFDD] hover:bg-[#F9F8F6] disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="orders-page-first"
              aria-label="Première page"
            >«</button>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage <= 1 || tableLoading}
              className="px-3 py-1.5 text-xs border border-[#E0DFDD] hover:bg-[#F9F8F6] disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="orders-page-prev"
              aria-label="Page précédente"
            >‹</button>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages || tableLoading}
              className="px-3 py-1.5 text-xs border border-[#E0DFDD] hover:bg-[#F9F8F6] disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="orders-page-next"
              aria-label="Page suivante"
            >›</button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage >= totalPages || tableLoading}
              className="px-3 py-1.5 text-xs border border-[#E0DFDD] hover:bg-[#F9F8F6] disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="orders-page-last"
              aria-label="Dernière page"
            >»</button>
          </div>
        </div>
      )}
    </div>
  );
};

// Promo Codes Management
export const AdminPromos = () => {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    min_order_amount: '',
    max_discount: '',
    usage_limit: '',
    per_user_limit: '1',
    valid_until: ''
  });

  const fetchPromos = async () => {
    try {
      const response = await adminFetch(`${API_URL}/api/bloom-gestion-secure/promos`);
      if (response.ok) {
        const data = await response.json();
        setPromos(data.promos || []);
      }
    } catch (error) {
      console.error('Error fetching promos:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromos();
  }, []);

  const resetForm = () => {
    setFormData({
      code: '',
      discount_type: 'percentage',
      discount_value: '',
      min_order_amount: '',
      max_discount: '',
      usage_limit: '',
      per_user_limit: '1',
      valid_until: ''
    });
    setEditingPromo(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const payload = {
      code: formData.code.toUpperCase(),
      discount_type: formData.discount_type,
      discount_value: parseFloat(formData.discount_value),
      min_order_amount: formData.min_order_amount ? parseFloat(formData.min_order_amount) : 0,
      max_discount: formData.max_discount ? parseFloat(formData.max_discount) : null,
      usage_limit: formData.usage_limit ? parseInt(formData.usage_limit) : null,
      per_user_limit: parseInt(formData.per_user_limit) || 1,
      valid_until: formData.valid_until || null
    };

    try {
      const url = editingPromo 
        ? `${API_URL}/api/bloom-gestion-secure/promos/${editingPromo.promo_id}`
        : `${API_URL}/api/bloom-gestion-secure/promos`;
      
      const response = await adminFetch(url, {
        method: editingPromo ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(editingPromo ? 'Code modifié' : 'Code créé');
        setShowForm(false);
        resetForm();
        fetchPromos();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (promo) => {
    setEditingPromo(promo);
    setFormData({
      code: promo.code,
      discount_type: promo.discount_type,
      discount_value: promo.discount_value.toString(),
      min_order_amount: promo.min_order_amount?.toString() || '',
      max_discount: promo.max_discount?.toString() || '',
      usage_limit: promo.usage_limit?.toString() || '',
      per_user_limit: promo.per_user_limit?.toString() || '1',
      valid_until: promo.valid_until ? promo.valid_until.slice(0, 10) : ''
    });
    setShowForm(true);
  };

  const handleToggleActive = async (promo) => {
    try {
      const response = await adminFetch(`${API_URL}/api/bloom-gestion-secure/promos/${promo.promo_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_active: !promo.is_active })
      });

      if (response.ok) {
        toast.success(promo.is_active ? 'Code désactivé' : 'Code activé');
        fetchPromos();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (promoId) => {
    if (!window.confirm('Supprimer ce code promo?')) return;

    try {
      const response = await adminFetch(`${API_URL}/api/bloom-gestion-secure/promos/${promoId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Code supprimé');
        fetchPromos();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  return (
    <div data-testid="admin-promos">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-3xl">Codes Promo</h1>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="btn-primary px-4 py-2 inline-flex items-center gap-2 text-sm"
          data-testid="new-promo-btn"
        >
          <Plus size={16} />
          Nouveau Code
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowForm(false)} />
          <div className="relative bg-white p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h2 className="font-serif text-2xl mb-6">
              {editingPromo ? 'Modifier le code' : 'Nouveau code promo'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm text-[#6B6A68] block mb-2">Code *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                  placeholder="BLOOM10"
                  className="checkout-input uppercase"
                  required
                  disabled={editingPromo}
                  data-testid="promo-code-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-[#6B6A68] block mb-2">Type</label>
                  <select
                    value={formData.discount_type}
                    onChange={(e) => setFormData({...formData, discount_type: e.target.value})}
                    className="checkout-input bg-transparent"
                  >
                    <option value="percentage">Pourcentage (%)</option>
                    <option value="fixed">Montant fixe (DA)</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-[#6B6A68] block mb-2">Valeur *</label>
                  <input
                    type="number"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({...formData, discount_value: e.target.value})}
                    placeholder={formData.discount_type === 'percentage' ? '10' : '500'}
                    className="checkout-input"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-[#6B6A68] block mb-2">Commande minimum (DA)</label>
                <input
                  type="number"
                  value={formData.min_order_amount}
                  onChange={(e) => setFormData({...formData, min_order_amount: e.target.value})}
                  placeholder="3000"
                  className="checkout-input"
                />
              </div>

              {formData.discount_type === 'percentage' && (
                <div>
                  <label className="text-sm text-[#6B6A68] block mb-2">Réduction max (DA)</label>
                  <input
                    type="number"
                    value={formData.max_discount}
                    onChange={(e) => setFormData({...formData, max_discount: e.target.value})}
                    placeholder="1000"
                    className="checkout-input"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-[#6B6A68] block mb-2">Utilisations max</label>
                  <input
                    type="number"
                    value={formData.usage_limit}
                    onChange={(e) => setFormData({...formData, usage_limit: e.target.value})}
                    placeholder="100"
                    className="checkout-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-[#6B6A68] block mb-2">Par utilisateur</label>
                  <input
                    type="number"
                    value={formData.per_user_limit}
                    onChange={(e) => setFormData({...formData, per_user_limit: e.target.value})}
                    placeholder="1"
                    className="checkout-input"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-[#6B6A68] block mb-2">Date d'expiration</label>
                <input
                  type="date"
                  value={formData.valid_until}
                  onChange={(e) => setFormData({...formData, valid_until: e.target.value})}
                  className="checkout-input"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 btn-secondary py-3 text-sm"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 btn-primary py-3 text-sm disabled:opacity-50"
                >
                  {saving ? <Loader2 className="animate-spin mx-auto" size={18} /> : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Promos List */}
      <div className="bg-white">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="animate-spin" />
          </div>
        ) : promos.length === 0 ? (
          <p className="text-[#6B6A68] text-center py-12">Aucun code promo</p>
        ) : (
          <table className="w-full">
            <thead className="border-b border-[#E0DFDD]">
              <tr>
                <th className="text-left p-4 font-medium text-sm">Code</th>
                <th className="text-left p-4 font-medium text-sm">Réduction</th>
                <th className="text-left p-4 font-medium text-sm">Utilisations</th>
                <th className="text-left p-4 font-medium text-sm">Statut</th>
                <th className="text-right p-4 font-medium text-sm">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E0DFDD]">
              {promos.map((promo) => (
                <tr key={promo.promo_id}>
                  <td className="p-4 font-mono font-medium">{promo.code}</td>
                  <td className="p-4 text-sm">
                    {promo.discount_type === 'percentage' 
                      ? `${promo.discount_value}%` 
                      : formatPrice(promo.discount_value)}
                    {promo.min_order_amount > 0 && (
                      <span className="text-xs text-[#6B6A68] block">
                        Min: {formatPrice(promo.min_order_amount)}
                      </span>
                    )}
                  </td>
                  <td className="p-4 text-sm">
                    {promo.usage_count}{promo.usage_limit ? `/${promo.usage_limit}` : ''}
                  </td>
                  <td className="p-4">
                    <button
                      onClick={() => handleToggleActive(promo)}
                      className={cn(
                        "text-xs px-2 py-1",
                        promo.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      )}
                    >
                      {promo.is_active ? 'Actif' : 'Inactif'}
                    </button>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => handleEdit(promo)} className="p-2 hover:bg-[#F5F4F2] rounded">
                        <Edit size={16} />
                      </button>
                      <button onClick={() => handleDelete(promo.promo_id)} className="p-2 hover:bg-red-50 text-[#C87979] rounded">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// Affiliate Dashboard Tab
const DashboardTab = ({ dashboard, expandedAffiliate, setExpandedAffiliate }) => {
  if (!dashboard) return <p className="text-[#6B6A68] text-center py-12">Chargement du dashboard...</p>;

  const { totals, affiliates } = dashboard;

  return (
    <div className="space-y-6" data-testid="affiliate-dashboard">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-5 border border-[#E0DFDD]">
          <p className="text-xs text-[#6B6A68] uppercase tracking-wider">Affiliés actifs</p>
          <p className="text-2xl font-serif mt-1" data-testid="kpi-active-affiliates">{totals.active_affiliates}</p>
        </div>
        <div className="bg-white p-5 border border-[#E0DFDD]">
          <p className="text-xs text-[#6B6A68] uppercase tracking-wider">Commandes via affiliation</p>
          <p className="text-2xl font-serif mt-1" data-testid="kpi-total-orders">{totals.total_orders}</p>
        </div>
        <div className="bg-white p-5 border border-[#E0DFDD]">
          <p className="text-xs text-[#6B6A68] uppercase tracking-wider">Commissions en attente</p>
          <p className="text-2xl font-serif mt-1 text-[#D4AF37]" data-testid="kpi-pending">{formatPrice(totals.pending_commissions)}</p>
        </div>
        <div className="bg-white p-5 border border-[#E0DFDD]">
          <p className="text-xs text-[#6B6A68] uppercase tracking-wider">Total payé</p>
          <p className="text-2xl font-serif mt-1 text-[#A3B19B]" data-testid="kpi-paid">{formatPrice(totals.paid_commissions)}</p>
        </div>
      </div>

      {/* Eligible vs Excluded Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-5 border border-[#E0DFDD]">
          <p className="text-xs text-[#6B6A68] uppercase tracking-wider">Montant total commandes</p>
          <p className="text-xl font-serif mt-1" data-testid="kpi-total-amount">{formatPrice(totals.total_order_amount)}</p>
        </div>
        <div className="bg-white p-5 border-l-4 border-[#A3B19B]">
          <p className="text-xs text-[#A3B19B] uppercase tracking-wider font-medium">Montant éligible</p>
          <p className="text-xl font-serif mt-1 text-[#A3B19B]" data-testid="kpi-eligible-amount">{formatPrice(totals.total_eligible_amount)}</p>
          <p className="text-xs text-[#6B6A68] mt-1">Soumis au calcul de commission</p>
        </div>
        <div className="bg-white p-5 border-l-4 border-[#C87979]">
          <p className="text-xs text-[#C87979] uppercase tracking-wider font-medium">Montant exclu (soldés)</p>
          <p className="text-xl font-serif mt-1 text-[#C87979]" data-testid="kpi-excluded-amount">{formatPrice(totals.total_excluded_amount)}</p>
          <p className="text-xs text-[#6B6A68] mt-1">Articles en promotion exclus</p>
        </div>
      </div>

      {/* Per-Affiliate Breakdown Table */}
      <div className="bg-white border border-[#E0DFDD]">
        <div className="p-4 border-b border-[#E0DFDD]">
          <h2 className="font-medium">Détail par affilié — Éligible vs Exclu</h2>
        </div>
        {affiliates.length === 0 ? (
          <p className="text-[#6B6A68] text-center py-8">Aucun affilié enregistré</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-[#E0DFDD] bg-[#F5F4F2]">
                <tr>
                  <th className="text-left p-3 text-xs font-medium">Affilié</th>
                  <th className="text-left p-3 text-xs font-medium">Code</th>
                  <th className="text-right p-3 text-xs font-medium">Cmd.</th>
                  <th className="text-right p-3 text-xs font-medium text-[#A3B19B]">Éligible</th>
                  <th className="text-right p-3 text-xs font-medium text-[#C87979]">Exclu</th>
                  <th className="text-right p-3 text-xs font-medium">% Exclu</th>
                  <th className="text-right p-3 text-xs font-medium text-[#D4AF37]">Commissions</th>
                  <th className="text-right p-3 text-xs font-medium">Payé</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E0DFDD]">
                {affiliates.map((aff) => (
                  <React.Fragment key={aff.affiliate_id}>
                    <tr
                      className="hover:bg-[#F5F4F2] cursor-pointer transition-colors"
                      onClick={() => setExpandedAffiliate(expandedAffiliate === aff.affiliate_id ? null : aff.affiliate_id)}
                      data-testid={`affiliate-row-${aff.affiliate_code}`}
                    >
                      <td className="p-3">
                        <p className="font-medium text-sm">{aff.user_name}</p>
                        <p className="text-xs text-[#6B6A68]">{aff.user_email}</p>
                      </td>
                      <td className="p-3 font-mono text-xs">{aff.affiliate_code}</td>
                      <td className="p-3 text-right text-sm">{aff.total_orders}</td>
                      <td className="p-3 text-right text-sm text-[#A3B19B]">{formatPrice(aff.eligible_amount)}</td>
                      <td className="p-3 text-right text-sm text-[#C87979]">{formatPrice(aff.excluded_amount)}</td>
                      <td className="p-3 text-right">
                        {aff.exclusion_ratio > 0 ? (
                          <span className={`text-xs px-2 py-0.5 ${
                            aff.exclusion_ratio > 50 ? 'bg-red-50 text-[#C87979]' :
                            aff.exclusion_ratio > 20 ? 'bg-amber-50 text-amber-600' :
                            'bg-gray-50 text-[#6B6A68]'
                          }`}>
                            {aff.exclusion_ratio}%
                          </span>
                        ) : (
                          <span className="text-xs text-[#6B6A68]">0%</span>
                        )}
                      </td>
                      <td className="p-3 text-right text-sm font-medium text-[#D4AF37]">{formatPrice(aff.total_commissions)}</td>
                      <td className="p-3 text-right text-sm">{formatPrice(aff.paid_commissions)}</td>
                    </tr>
                    {expandedAffiliate === aff.affiliate_id && aff.recent_referrals.length > 0 && (
                      <tr>
                        <td colSpan={8} className="p-0">
                          <div className="bg-[#F5F4F2] p-4">
                            <p className="text-xs font-medium text-[#6B6A68] mb-3">Dernières conversions — {aff.user_name}</p>
                            <table className="w-full">
                              <thead>
                                <tr className="text-xs text-[#6B6A68]">
                                  <th className="text-left pb-2">Commande</th>
                                  <th className="text-right pb-2">Total</th>
                                  <th className="text-right pb-2 text-[#A3B19B]">Éligible</th>
                                  <th className="text-right pb-2 text-[#C87979]">Exclu</th>
                                  <th className="text-right pb-2">Commission</th>
                                  <th className="text-right pb-2">Statut</th>
                                  <th className="text-right pb-2">Date</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-[#E0DFDD]">
                                {aff.recent_referrals.map((ref, idx) => (
                                  <tr key={idx} className="text-xs">
                                    <td className="py-2 font-mono">{ref.order_id || '—'}</td>
                                    <td className="py-2 text-right">{formatPrice(ref.order_amount)}</td>
                                    <td className="py-2 text-right text-[#A3B19B]">{formatPrice(ref.eligible_amount)}</td>
                                    <td className="py-2 text-right text-[#C87979]">{formatPrice(ref.excluded_amount)}</td>
                                    <td className="py-2 text-right font-medium">{formatPrice(ref.commission_amount)}</td>
                                    <td className="py-2 text-right">
                                      <span className={`px-1.5 py-0.5 ${
                                        ref.commission_status === 'paid' ? 'bg-green-100 text-green-700' :
                                        ref.commission_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                        'bg-gray-100 text-[#6B6A68]'
                                      }`}>
                                        {ref.commission_status === 'paid' ? 'Payé' :
                                         ref.commission_status === 'pending' ? 'En attente' :
                                         ref.commission_status === 'not_eligible' ? 'Non éligible' : ref.commission_status}
                                      </span>
                                    </td>
                                    <td className="py-2 text-right text-[#6B6A68]">{formatDate(ref.created_at)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};


// Categories Management — Admin can update category images + site logo
export const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [savingLogo, setSavingLogo] = useState(false);
  const [banners, setBanners] = useState({ desktop_image: '', mobile_image: '', overlay_title: '', overlay_subtitle: '', overlay_button_text: '', overlay_button_link: '/shop' });
  const [savingBanners, setSavingBanners] = useState(false);
  const [activeTab, setActiveTab] = useState('logo');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, settingsRes, bannerRes] = await Promise.all([
          fetch(`${API_URL}/api/categories`),
          fetch(`${API_URL}/api/site-settings`),
          fetch(`${API_URL}/api/site-banners`)
        ]);
        if (catRes.ok) {
          const data = await catRes.json();
          setCategories(data.categories || []);
        }
        if (settingsRes.ok) {
          const data = await settingsRes.json();
          setLogoUrl(data.logo_url || '');
        }
        if (bannerRes.ok) {
          const data = await bannerRes.json();
          setBanners(prev => ({ ...prev, ...data }));
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSaveLogo = async (url) => {
    setSavingLogo(true);
    try {
      const res = await adminFetch(`${API_URL}/api/bloom-gestion-secure/site-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ logo_url: url || logoUrl })
      });
      if (res.ok) { setLogoUrl(url || logoUrl); toast.success('Logo mis à jour'); }
      else toast.error('Erreur');
    } catch (error) {
      toast.error('Erreur réseau');
    } finally {
      setSavingLogo(false);
    }
  };

  const handleCategoryImageUpload = async (categoryId, result) => {
    const imageUrl = result.url;
    setSaving(categoryId);
    try {
      const res = await adminFetch(`${API_URL}/api/bloom-gestion-secure/categories/${categoryId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ image: imageUrl })
      });
      if (res.ok) {
        setCategories(prev => prev.map(c =>
          c.id === categoryId ? { ...c, image: imageUrl } : c
        ));
        toast.success('Image de catégorie mise à jour');
      } else {
        toast.error('Erreur lors de la mise à jour');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    } finally {
      setSaving(null);
    }
  };

  const handleSaveBanners = async () => {
    setSavingBanners(true);
    try {
      const res = await adminFetch(`${API_URL}/api/bloom-gestion-secure/site-banners`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(banners)
      });
      if (res.ok) toast.success('Bannières mises à jour');
      else toast.error('Erreur');
    } catch (error) {
      toast.error('Erreur réseau');
    } finally {
      setSavingBanners(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin" /></div>;

  const tabs = [
    { id: 'logo', label: 'Logo & Marque' },
    { id: 'categories', label: 'Catégories' },
    { id: 'banners', label: 'Bannières' },
  ];

  return (
    <div data-testid="admin-categories">
      <h1 className="font-serif text-3xl mb-2">Apparence du site</h1>
      <p className="text-sm text-[#6B6A68] mb-6">Gérez le logo, les images des catégories et les bannières</p>

      {/* Tabs */}
      <div className="flex gap-1 mb-8 border-b border-[#E0DFDD]">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-5 py-3 text-sm font-medium transition-colors border-b-2 -mb-[1px] ${
              activeTab === tab.id
                ? 'border-[#D4AF37] text-[#1A1514]'
                : 'border-transparent text-[#6B6A68] hover:text-[#1A1514]'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Logo & Brand Tab */}
      {activeTab === 'logo' && (
        <div className="space-y-6" data-testid="logo-settings">
          <div className="bg-white border border-[#E0DFDD] p-6">
            <h2 className="font-medium mb-2">Logo & Identité de marque</h2>
            <p className="text-xs text-[#6B6A68] mb-6">Uploadez votre logo (PNG avec fond transparent recommandé)</p>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Upload zone */}
              <div>
                <ImageUploadZone
                  uploadType="logo"
                  label="Uploadez votre logo (PNG, SVG, JPG)"
                  onUploaded={(result) => {
                    const url = result.url;
                    setLogoUrl(url);
                    handleSaveLogo(url);
                  }}
                />
              </div>

              {/* Preview */}
              <div>
                <p className="text-xs text-[#6B6A68] mb-3 uppercase tracking-wider">Aperçu dans le header</p>
                <div className="bg-[#1A1514] p-4 flex items-center justify-center h-20 border" data-testid="logo-preview-dark">
                  {logoUrl ? (
                    <img src={logoUrl} alt="Logo preview" className="max-h-12 max-w-[180px] object-contain" />
                  ) : (
                    <span className="font-serif text-2xl text-white">Bloom<span className="text-[#D4AF37]">.</span></span>
                  )}
                </div>
                <div className="bg-white p-4 flex items-center justify-center h-20 border mt-2" data-testid="logo-preview-light">
                  {logoUrl ? (
                    <img src={logoUrl} alt="Logo preview" className="max-h-12 max-w-[180px] object-contain" />
                  ) : (
                    <span className="font-serif text-2xl text-[#1A1514]">Bloom<span className="text-[#D4AF37]">.</span></span>
                  )}
                </div>
                {logoUrl && (
                  <button
                    onClick={() => { setLogoUrl(''); handleSaveLogo(''); }}
                    className="mt-3 text-xs text-[#C87979] hover:underline"
                    data-testid="logo-remove-btn"
                  >
                    Supprimer le logo (revenir au défaut)
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Categories Tab */}
      {activeTab === 'categories' && (
        <div data-testid="categories-images">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.filter(c => c.id !== 'nouvel-arrivage' && c.id !== 'promotion').map((category) => (
              <div key={category.id} className="bg-white border border-[#E0DFDD] overflow-hidden" data-testid={`cat-card-${category.id}`}>
                <div className="aspect-[3/4] relative bg-[#F5F4F2] overflow-hidden">
                  {category.image ? (
                    <img
                      src={resolveImageUrl(category.image)}
                      alt={category.name}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImagePlus size={32} className="text-[#9E9C9A]" />
                    </div>
                  )}
                  {saving === category.id && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <Loader2 className="animate-spin text-white" />
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-medium text-sm mb-1">{category.name}</h3>
                  <p className="text-xs text-[#6B6A68] mb-3">ID: {category.id}</p>
                  <ImageUploadZone
                    uploadType="categories"
                    compact={false}
                    label="Déposez l'image de catégorie ici"
                    onUploaded={(result) => handleCategoryImageUpload(category.id, result)}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Banners Tab */}
      {activeTab === 'banners' && (
        <div className="space-y-6" data-testid="banners-settings">
          <div className="bg-white border border-[#E0DFDD] p-6">
            <h2 className="font-medium mb-2">Bannière Hero (Page d'accueil)</h2>
            <p className="text-xs text-[#6B6A68] mb-6">La grande image en haut de la page d'accueil</p>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Desktop banner */}
              <div>
                <p className="text-xs font-medium mb-2 uppercase tracking-wider">Version Desktop (1920x1080px recommandé)</p>
                {banners.desktop_image && (
                  <div className="aspect-video mb-3 bg-[#F5F4F2] overflow-hidden border" data-testid="banner-desktop-preview">
                    <img src={resolveImageUrl(banners.desktop_image)} alt="Desktop banner" className="w-full h-full object-cover" />
                  </div>
                )}
                <ImageUploadZone
                  uploadType="banners"
                  label="Déposez la bannière desktop ici"
                  onUploaded={(result) => {
                    const url = result.url;
                    setBanners(prev => ({ ...prev, desktop_image: url }));
                  }}
                />
              </div>

              {/* Mobile banner */}
              <div>
                <p className="text-xs font-medium mb-2 uppercase tracking-wider">Version Mobile (768x1024px recommandé)</p>
                {banners.mobile_image && (
                  <div className="aspect-[3/4] max-w-[200px] mb-3 bg-[#F5F4F2] overflow-hidden border" data-testid="banner-mobile-preview">
                    <img src={banners.mobile_image} alt="Mobile banner" className="w-full h-full object-cover" />
                  </div>
                )}
                <ImageUploadZone
                  uploadType="banners"
                  label="Déposez la bannière mobile ici"
                  onUploaded={(result) => {
                    const url = result.url;
                    setBanners(prev => ({ ...prev, mobile_image: url }));
                  }}
                />
              </div>
            </div>

            {/* Text overlay settings */}
            <div className="mt-8 pt-6 border-t border-[#E0DFDD]">
              <h3 className="font-medium text-sm mb-4">Texte sur la bannière</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-[#6B6A68] block mb-1">Titre principal</label>
                  <input
                    type="text"
                    value={banners.overlay_title}
                    onChange={(e) => setBanners(prev => ({ ...prev, overlay_title: e.target.value }))}
                    placeholder="La lingerie que tu mérites."
                    className="w-full text-sm border border-[#E0DFDD] px-4 py-2.5 focus:outline-none focus:border-[#D4AF37]"
                    data-testid="banner-overlay-title"
                  />
                </div>
                <div>
                  <label className="text-xs text-[#6B6A68] block mb-1">Sous-titre</label>
                  <input
                    type="text"
                    value={banners.overlay_subtitle}
                    onChange={(e) => setBanners(prev => ({ ...prev, overlay_subtitle: e.target.value }))}
                    placeholder="Qualité premium. Tailles S à XXXL."
                    className="w-full text-sm border border-[#E0DFDD] px-4 py-2.5 focus:outline-none focus:border-[#D4AF37]"
                    data-testid="banner-overlay-subtitle"
                  />
                </div>
                <div>
                  <label className="text-xs text-[#6B6A68] block mb-1">Texte du bouton</label>
                  <input
                    type="text"
                    value={banners.overlay_button_text}
                    onChange={(e) => setBanners(prev => ({ ...prev, overlay_button_text: e.target.value }))}
                    placeholder="Découvrir la Collection"
                    className="w-full text-sm border border-[#E0DFDD] px-4 py-2.5 focus:outline-none focus:border-[#D4AF37]"
                    data-testid="banner-overlay-button-text"
                  />
                </div>
                <div>
                  <label className="text-xs text-[#6B6A68] block mb-1">Lien du bouton</label>
                  <input
                    type="text"
                    value={banners.overlay_button_link}
                    onChange={(e) => setBanners(prev => ({ ...prev, overlay_button_link: e.target.value }))}
                    placeholder="/shop"
                    className="w-full text-sm border border-[#E0DFDD] px-4 py-2.5 focus:outline-none focus:border-[#D4AF37]"
                    data-testid="banner-overlay-button-link"
                  />
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={handleSaveBanners}
                disabled={savingBanners}
                className="px-8 py-3 bg-[#1A1514] text-white text-sm hover:bg-[#D4AF37] transition-colors disabled:opacity-50"
                data-testid="save-banners-btn"
              >
                {savingBanners ? <Loader2 size={16} className="animate-spin mx-auto" /> : 'Sauvegarder les bannières'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};



// Affiliates Management
export const AdminAffiliates = () => {
  const [affiliates, setAffiliates] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [expandedAffiliate, setExpandedAffiliate] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAffiliate, setEditingAffiliate] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', instagram: '', tiktok: '',
    affiliate_code: '', commission_rate: 10, discount_type: 'percentage', discount_value: 10
  });
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [affRes, wdrRes, dashRes, syncRes] = await Promise.all([
        adminFetch(`${API_URL}/api/bloom-gestion-secure/affiliates`),
        adminFetch(`${API_URL}/api/bloom-gestion-secure/withdrawals`),
        adminFetch(`${API_URL}/api/bloom-gestion-secure/affiliates/dashboard`),
        adminFetch(`${API_URL}/api/bloom-gestion-secure/affiliate/sync-status`)
      ]);
      if (affRes.ok) { const d = await affRes.json(); setAffiliates(d.affiliates || []); }
      if (wdrRes.ok) { const d = await wdrRes.json(); setWithdrawals(d.withdrawals || []); }
      if (dashRes.ok) { const d = await dashRes.json(); setDashboard(d); }
      if (syncRes.ok) { const d = await syncRes.json(); setSyncStatus(d); }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setFormData({ name: '', email: '', instagram: '', tiktok: '', affiliate_code: '', commission_rate: 10, discount_type: 'percentage', discount_value: 10 });
    setShowAddForm(false);
    setEditingAffiliate(null);
  };

  const handleSubmitAffiliate = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { toast.error('Le nom est requis'); return; }
    setFormLoading(true);
    try {
      const isEdit = !!editingAffiliate;
      const url = isEdit
        ? `${API_URL}/api/bloom-gestion-secure/affiliate/${editingAffiliate.affiliate_id}`
        : `${API_URL}/api/bloom-gestion-secure/affiliate/create`;
      const response = await adminFetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          instagram: formData.instagram.trim(),
          tiktok: formData.tiktok.trim(),
          ...(isEdit ? {} : { affiliate_code: formData.affiliate_code.trim() || undefined }),
          commission_rate: parseFloat(formData.commission_rate) || 10,
          discount_type: formData.discount_type,
          discount_value: parseFloat(formData.discount_value) || 10,
          ...(isEdit ? { status: editingAffiliate.status } : {}),
        })
      });
      if (response.ok) {
        toast.success(isEdit ? 'Affilié mis à jour' : 'Affilié créé avec succès');
        resetForm();
        setLoading(true);
        fetchData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch { toast.error('Erreur de connexion'); }
    finally { setFormLoading(false); }
  };

  const handleEditClick = (aff) => {
    setEditingAffiliate(aff);
    setFormData({
      name: aff.name || aff.user_name || '',
      email: aff.email || aff.user_email || '',
      instagram: aff.instagram || '',
      tiktok: aff.tiktok || '',
      affiliate_code: aff.affiliate_code || '',
      commission_rate: aff.commission_rate || 10,
      discount_type: aff.discount_type || 'percentage',
      discount_value: aff.discount_value || 10,
    });
    setShowAddForm(true);
    setActiveTab('affiliates');
  };

  const handleDeleteAffiliate = async (affiliateId) => {
    if (!window.confirm('Supprimer cet affilié ?')) return;
    try {
      const response = await adminFetch(`${API_URL}/api/bloom-gestion-secure/affiliate/${affiliateId}`, {
        method: 'DELETE', credentials: 'include'
      });
      if (response.ok) {
        toast.success('Affilié supprimé');
        setLoading(true);
        fetchData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch { toast.error('Erreur de connexion'); }
  };

  const handleStatusChange = async (aff, newStatus) => {
    try {
      const response = await adminFetch(`${API_URL}/api/bloom-gestion-secure/affiliate/${aff.affiliate_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        toast.success(`Statut mis à jour: ${newStatus}`);
        setLoading(true);
        fetchData();
      }
    } catch { toast.error('Erreur'); }
  };

  const handleWithdrawalAction = async (withdrawalId, status) => {
    try {
      const response = await adminFetch(`${API_URL}/api/bloom-gestion-secure/withdrawals/${withdrawalId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status })
      });
      if (response.ok) {
        toast.success(`Retrait ${status === 'paid' ? 'marqué comme payé' : status === 'rejected' ? 'refusé' : 'approuvé'}`);
        setLoading(true);
        fetchData();
      }
    } catch { toast.error('Erreur'); }
  };

  const handleForceSync = async () => {
    setSyncing(true);
    try {
      const res = await adminFetch(`${API_URL}/api/bloom-gestion-secure/affiliate/force-sync`, {
        method: 'POST', credentials: 'include'
      });
      const data = await res.json();
      toast.success(data.message || 'Sync terminé');
      fetchData();
    } catch { toast.error('Erreur sync'); }
    finally { setSyncing(false); }
  };

  const pendingWithdrawals = withdrawals.filter(w => w.status === 'pending' || w.status === 'approved');

  return (
    <div data-testid="admin-affiliates">
      <h1 className="font-serif text-3xl mb-8">Programme Affiliation</h1>

      {/* Tabs */}
      <div className="flex gap-3 mb-6">
        <button
          onClick={() => setActiveTab('dashboard')}
          className={cn(
            "px-4 py-2 text-sm",
            activeTab === 'dashboard' ? 'bg-[#1A1514] text-white' : 'bg-white border'
          )}
          data-testid="tab-dashboard"
        >
          Dashboard
        </button>
        <button
          onClick={() => setActiveTab('affiliates')}
          className={cn(
            "px-4 py-2 text-sm",
            activeTab === 'affiliates' ? 'bg-[#1A1514] text-white' : 'bg-white border'
          )}
          data-testid="tab-affiliates"
        >
          Affiliés ({affiliates.length})
        </button>
        <button
          onClick={() => setActiveTab('withdrawals')}
          className={cn(
            "px-4 py-2 text-sm relative",
            activeTab === 'withdrawals' ? 'bg-[#1A1514] text-white' : 'bg-white border'
          )}
          data-testid="tab-withdrawals"
        >
          Retraits
          {pendingWithdrawals.length > 0 && (
            <span className="absolute -top-2 -right-2 w-5 h-5 bg-[#D4AF37] text-[#1A1514] text-xs rounded-full flex items-center justify-center">
              {pendingWithdrawals.length}
            </span>
          )}
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin" />
        </div>
      ) : activeTab === 'dashboard' ? (
        <div className="space-y-6">
          {/* Ecotrack Sync Status */}
          {syncStatus && (
            <div className="bg-white border border-[#E0DFDD] p-4 flex items-center justify-between" data-testid="sync-status-bar">
              <div className="flex items-center gap-6 text-sm">
                <span className="text-[#6B6A68]">Sync Ecotrack:</span>
                <span className={syncStatus.token_configured ? 'text-green-600' : 'text-amber-600'}>
                  {syncStatus.token_configured ? 'Token OK' : 'Token non configuré'}
                </span>
                <span>En attente: <strong className="text-[#D4AF37]">{syncStatus.pending_commissions}</strong></span>
                <span>Créditées: <strong className="text-green-600">{syncStatus.credited_commissions}</strong></span>
                <span>Annulées: <strong className="text-[#C87979]">{syncStatus.cancelled_commissions}</strong></span>
              </div>
              <button
                onClick={handleForceSync}
                disabled={syncing || !syncStatus.token_configured}
                className="px-4 py-2 text-xs font-medium bg-[#1A1514] text-white disabled:opacity-40 flex items-center gap-2"
                data-testid="force-sync-btn"
              >
                {syncing ? <Loader2 size={12} className="animate-spin" /> : null}
                Sync manuel
              </button>
            </div>
          )}
          <DashboardTab dashboard={dashboard} expandedAffiliate={expandedAffiliate} setExpandedAffiliate={setExpandedAffiliate} />
        </div>
      ) : activeTab === 'affiliates' ? (
        <div className="space-y-4">
          {/* Add/Edit Form */}
          {showAddForm ? (
            <form onSubmit={handleSubmitAffiliate} className="bg-white border border-[#E0DFDD] p-6 space-y-4" data-testid="affiliate-form">
              <div className="flex items-center justify-between mb-2">
                <h2 className="font-serif text-lg">{editingAffiliate ? 'Modifier l\'affilié' : 'Ajouter un affilié / influenceur'}</h2>
                <button type="button" onClick={resetForm} className="p-1 hover:bg-gray-100 rounded"><X size={18} /></button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium block mb-1">Nom <span className="text-red-500">*</span></label>
                  <input type="text" value={formData.name} onChange={e => setFormData(p => ({...p, name: e.target.value}))} placeholder="Nom complet" className="w-full border border-[#E0DFDD] p-2.5 text-sm focus:outline-none focus:border-[#1A1514]" data-testid="aff-input-name" />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Email</label>
                  <input type="email" value={formData.email} onChange={e => setFormData(p => ({...p, email: e.target.value}))} placeholder="email@exemple.com" className="w-full border border-[#E0DFDD] p-2.5 text-sm focus:outline-none focus:border-[#1A1514]" data-testid="aff-input-email" />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Instagram</label>
                  <input type="text" value={formData.instagram} onChange={e => setFormData(p => ({...p, instagram: e.target.value}))} placeholder="@handle" className="w-full border border-[#E0DFDD] p-2.5 text-sm focus:outline-none focus:border-[#1A1514]" data-testid="aff-input-instagram" />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">TikTok</label>
                  <input type="text" value={formData.tiktok} onChange={e => setFormData(p => ({...p, tiktok: e.target.value}))} placeholder="@handle" className="w-full border border-[#E0DFDD] p-2.5 text-sm focus:outline-none focus:border-[#1A1514]" data-testid="aff-input-tiktok" />
                </div>
                {!editingAffiliate && (
                  <div>
                    <label className="text-sm font-medium block mb-1">Code promo (auto si vide)</label>
                    <input type="text" value={formData.affiliate_code} onChange={e => setFormData(p => ({...p, affiliate_code: e.target.value.toUpperCase()}))} placeholder="Ex: SARA10" className="w-full border border-[#E0DFDD] p-2.5 text-sm font-mono focus:outline-none focus:border-[#1A1514]" data-testid="aff-input-code" />
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium block mb-1">Taux commission (%)</label>
                  <input type="number" value={formData.commission_rate} onChange={e => setFormData(p => ({...p, commission_rate: e.target.value}))} min="0" max="50" step="0.5" className="w-full border border-[#E0DFDD] p-2.5 text-sm focus:outline-none focus:border-[#1A1514]" data-testid="aff-input-commission" />
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Type remise client</label>
                  <select value={formData.discount_type} onChange={e => setFormData(p => ({...p, discount_type: e.target.value}))} className="w-full border border-[#E0DFDD] p-2.5 text-sm bg-white focus:outline-none focus:border-[#1A1514]" data-testid="aff-input-discount-type">
                    <option value="percentage">Pourcentage (%)</option>
                    <option value="fixed">Montant fixe (DA)</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Valeur remise client</label>
                  <input type="number" value={formData.discount_value} onChange={e => setFormData(p => ({...p, discount_value: e.target.value}))} min="0" step="0.5" className="w-full border border-[#E0DFDD] p-2.5 text-sm focus:outline-none focus:border-[#1A1514]" data-testid="aff-input-discount-value" />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={resetForm} className="px-4 py-2.5 border border-[#E0DFDD] text-sm">Annuler</button>
                <button type="submit" disabled={formLoading} className="px-6 py-2.5 bg-[#1A1514] text-white text-sm font-medium disabled:opacity-50 flex items-center gap-2" data-testid="aff-submit-btn">
                  {formLoading ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  {editingAffiliate ? 'Mettre à jour' : 'Créer l\'affilié'}
                </button>
              </div>
            </form>
          ) : (
            <button onClick={() => { resetForm(); setShowAddForm(true); }} className="flex items-center gap-2 bg-[#1A1514] text-white px-4 py-2.5 text-sm font-medium" data-testid="add-affiliate-btn">
              <Plus size={16} /> Ajouter un affilié
            </button>
          )}

          {/* Affiliates Table */}
          <div className="bg-white border border-[#E0DFDD]">
            {affiliates.length === 0 ? (
              <p className="text-[#6B6A68] text-center py-12">Aucun affilié</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-[#E0DFDD] bg-[#F5F4F2]">
                    <tr>
                      <th className="text-left p-3 text-xs font-medium">Affilié</th>
                      <th className="text-left p-3 text-xs font-medium">Code</th>
                      <th className="text-left p-3 text-xs font-medium">Réseaux</th>
                      <th className="text-right p-3 text-xs font-medium">Commission</th>
                      <th className="text-right p-3 text-xs font-medium">Remise client</th>
                      <th className="text-right p-3 text-xs font-medium">Commandes</th>
                      <th className="text-right p-3 text-xs font-medium text-[#D4AF37]">En attente</th>
                      <th className="text-right p-3 text-xs font-medium">Payé</th>
                      <th className="text-center p-3 text-xs font-medium">Statut</th>
                      <th className="text-right p-3 text-xs font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E0DFDD]">
                    {affiliates.map((aff) => (
                      <tr key={aff.affiliate_id} className="hover:bg-[#F5F4F2]" data-testid={`aff-row-${aff.affiliate_code}`}>
                        <td className="p-3">
                          <p className="font-medium text-sm">{aff.name || aff.user_name || 'N/A'}</p>
                          <p className="text-xs text-[#6B6A68]">{aff.email || aff.user_email}</p>
                        </td>
                        <td className="p-3 font-mono text-xs font-medium">{aff.affiliate_code}</td>
                        <td className="p-3">
                          <div className="flex flex-col gap-0.5">
                            {aff.instagram && <span className="text-xs text-pink-600">IG: {aff.instagram}</span>}
                            {aff.tiktok && <span className="text-xs text-[#6B6A68]">TT: {aff.tiktok}</span>}
                            {!aff.instagram && !aff.tiktok && <span className="text-xs text-[#6B6A68]">-</span>}
                          </div>
                        </td>
                        <td className="p-3 text-right text-sm">{aff.commission_rate}%</td>
                        <td className="p-3 text-right text-sm">{aff.discount_value || 10}{(aff.discount_type || 'percentage') === 'percentage' ? '%' : ' DA'}</td>
                        <td className="p-3 text-right text-sm">{aff.total_orders}</td>
                        <td className="p-3 text-right text-sm text-[#D4AF37] font-medium">{formatPrice(aff.pending_earnings)}</td>
                        <td className="p-3 text-right text-sm">{formatPrice(aff.paid_earnings)}</td>
                        <td className="p-3 text-center">
                          <select
                            value={aff.status}
                            onChange={e => handleStatusChange(aff, e.target.value)}
                            className={cn("text-xs px-2 py-1 border-0 rounded cursor-pointer",
                              aff.status === 'active' ? 'bg-green-100 text-green-700' :
                              aff.status === 'suspended' ? 'bg-red-100 text-red-700' :
                              'bg-yellow-100 text-yellow-700'
                            )}
                            data-testid={`aff-status-${aff.affiliate_code}`}
                          >
                            <option value="active">Actif</option>
                            <option value="pending">En attente</option>
                            <option value="suspended">Suspendu</option>
                          </select>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => handleEditClick(aff)} className="p-1.5 hover:bg-blue-50 text-blue-600 rounded" title="Modifier" data-testid={`aff-edit-${aff.affiliate_code}`}><Edit size={14} /></button>
                            <button onClick={() => handleDeleteAffiliate(aff.affiliate_id)} className="p-1.5 hover:bg-red-50 text-[#C87979] rounded" title="Supprimer" data-testid={`aff-delete-${aff.affiliate_code}`}><Trash2 size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white">
          {withdrawals.length === 0 ? (
            <p className="text-[#6B6A68] text-center py-12">Aucune demande de retrait</p>
          ) : (
            <table className="w-full">
              <thead className="border-b border-[#E0DFDD]">
                <tr>
                  <th className="text-left p-4 font-medium text-sm">ID</th>
                  <th className="text-left p-4 font-medium text-sm">Affilié</th>
                  <th className="text-left p-4 font-medium text-sm">Montant</th>
                  <th className="text-left p-4 font-medium text-sm">Date</th>
                  <th className="text-left p-4 font-medium text-sm">Statut</th>
                  <th className="text-right p-4 font-medium text-sm">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E0DFDD]">
                {withdrawals.map((w) => {
                  const aff = affiliates.find(a => a.affiliate_id === w.affiliate_id);
                  return (
                    <tr key={w.withdrawal_id}>
                      <td className="p-4 font-mono text-xs">{w.withdrawal_id}</td>
                      <td className="p-4 text-sm">{aff?.user_name || aff?.affiliate_code || 'N/A'}</td>
                      <td className="p-4 font-medium">{formatPrice(w.amount)}</td>
                      <td className="p-4 text-sm text-[#6B6A68]">{formatDate(w.created_at)}</td>
                      <td className="p-4">
                        <span className={cn(
                          "text-xs px-2 py-1",
                          w.status === 'paid' ? 'bg-green-100 text-green-700' :
                          w.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          w.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                          'bg-red-100 text-red-700'
                        )}>
                          {w.status === 'paid' ? 'Payé' :
                           w.status === 'pending' ? 'En attente' :
                           w.status === 'approved' ? 'Approuvé' : 'Refusé'}
                        </span>
                      </td>
                      <td className="p-4">
                        {(w.status === 'pending' || w.status === 'approved') && (
                          <div className="flex items-center justify-end gap-2">
                            {w.status === 'pending' && (
                              <button
                                onClick={() => handleWithdrawalAction(w.withdrawal_id, 'approved')}
                                className="p-2 hover:bg-blue-50 text-blue-600 rounded"
                                title="Approuver"
                              >
                                <Check size={16} />
                              </button>
                            )}
                            <button
                              onClick={() => handleWithdrawalAction(w.withdrawal_id, 'paid')}
                              className="p-2 hover:bg-green-50 text-green-600 rounded"
                              title="Marquer comme payé"
                            >
                              <DollarSign size={16} />
                            </button>
                            <button
                              onClick={() => handleWithdrawalAction(w.withdrawal_id, 'rejected')}
                              className="p-2 hover:bg-red-50 text-[#C87979] rounded"
                              title="Refuser"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

// Main Admin Layout
export default function AdminPage() {
  const { user, loading, logout } = useAuth();
  // Treat OAuth user as the "admin" user dict for the rest of this page
  const admin = user;
  const navigate = useNavigate();
  const inactivityTimer = useRef(null);
  const ADMIN_TIMEOUT = 30 * 60 * 1000; // 30 minutes

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      toast.error('Session admin expirée pour inactivité.');
      if (logout) logout();
      navigate('/bloom-gestion-secure');
    }, ADMIN_TIMEOUT);
  }, [navigate, logout]);

  useEffect(() => {
    if (!loading && !admin) {
      return; // RequireAdminWithStepUp handles redirect to OAuth/TOTP
    }

    // Admin inactivity auto-logout
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    events.forEach(e => document.addEventListener(e, resetInactivityTimer));
    resetInactivityTimer();

    return () => {
      events.forEach(e => document.removeEventListener(e, resetInactivityTimer));
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    };
  }, [admin, loading, navigate, resetInactivityTimer]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FCFBF9] flex items-center justify-center">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  if (!admin) {
    return null; // AdminAuthGate will show login
  }

  return (
    <div className="flex min-h-screen bg-[#FCFBF9]">
      <AdminSidebar />
      <main className="flex-1 p-8">
        <Outlet />
      </main>
    </div>
  );
}
